import React from 'react';
import styled from 'styled-components';
import { ExclamationCircleIcon } from '@interstate/components/Icons/ExclamationCircleIcon';
import { Table } from '@interstate/components/Table';
import type { TableData } from '../../utils';
import { TEST_IDS } from '../../../constants';
import { Popover } from '@interstate/components/Popover';
import './TemplateOptionsPopover.css';

const IconWrapper = styled.div`
    cursor: pointer;
    margin-left: 4px;
    margin-top: 3px;
`;

const mapTemplateCombinations = (details: TableData['details']) => {
    const inventoryTypes = details!.inventoryTypes || ['All'];
    const years = details!.years || ['All'];
    const makes = details!.makes || ['All'];
    const models = details!.models || ['All'];
    const trims = details!.trims || ['All'];
    const configurations = details!.configurations || ['All'];

    const sortedInventoryTypes = [...inventoryTypes].sort((a, b) => {
        const order = { New: 1, CertifiedPreOwned: 2, Used: 3 };
        const aOrder = order[a as keyof typeof order] || 4;
        const bOrder = order[b as keyof typeof order] || 4;
        return aOrder - bOrder;
    });

    const sortedYears = [...years].sort((a, b) => {
        if (a === 'All' || b === 'All') return 0;
        return parseInt(String(a)) - parseInt(String(b));
    });

    return sortedInventoryTypes.flatMap((inventoryType) =>
        sortedYears.flatMap((year) =>
            makes.flatMap((make) =>
                models.flatMap((model) =>
                    trims.flatMap((trim) =>
                        configurations.map((configuration) => ({
                            inventoryTypes: String(inventoryType),
                            years: String(year),
                            makes: String(make),
                            models: String(model),
                            trims: String(trim),
                            configurations: String(configuration)
                        }))
                    )
                )
            )
        )
    );
};

export const TemplateCombinationPopover = ({ details }: any) => {
    const columns = [
        {
            dataIndex: 'inventoryTypes',
            title: 'Type',
            render: (type: string) => <>{type}</>
        },
        {
            dataIndex: 'years',
            title: 'Year',
            render: (year: string) => <>{year}</>
        },
        {
            dataIndex: 'makes',
            title: 'Make',
            render: (make: string) => <>{make}</>
        },
        {
            dataIndex: 'models',
            title: 'Model',
            render: (model: string) => <>{model}</>
        },
        {
            dataIndex: 'trims',
            title: 'Trim',
            render: (trim: string) => <>{trim}</>
        },
        {
            dataIndex: 'configurations',
            title: 'Configuration',
            render: (configuration: string) => <>{configuration}</>
        }
    ];

    return (
        <Popover
            data-testid={TEST_IDS.TEMPLATE_NAME_POPOVER}
            position="bottom"
            trigger="outsideClick"
            className="template-options-popover"
            popoverContent={
                <Table
                    data={mapTemplateCombinations(details)}
                    tableLayout="auto"
                    columns={columns}
                    emptyText="No Deal Templates combinations found"
                    enablePagination={false}
                />
            }
        >
            <IconWrapper data-testid="template-combination-icon">
                <ExclamationCircleIcon />
            </IconWrapper>
        </Popover>
    );
};
